/* eslint-disable class-methods-use-this */
import Page from './navigation/default-page';

import Transitions from './navigation/transitions';

// Import the transitions used by the Router in this application
import TransitionDefault from './navigation/transitions/default';
import TransitionDefaultToHomepage from './navigation/transitions/default-to-homepage';
import TransitionOverviewToStory from './navigation/transitions/overview-to-story';
import TransitionDefaultFromMenu from './navigation/transitions/default-from-menu';
import TransitionHomepageSliderToWork from './navigation/transitions/homepage-slider-to-work';
import TransitionContextSliderToWork from './navigation/transitions/context-slider-to-work';
import TransitionRelatedStory from './navigation/transitions/related-story';
// import TransitionFromMenuDefault from "./navigation/transitions/from-menu-default";
// import TransitionFromPortfolioToWork from "./navigation/transitions/portfolio-to-work";
// import TransitionSidebarLogoToHome from "./navigation/transitions/sidebar-logo-to-home";
// import TransitionWorkDetailToWorkOverview from "./navigation/transitions/work-detail-to-work-overview";

import { cacheAdapterEnhancer, throttleAdapterEnhancer } from 'axios-extensions';

/**
 * The Router class handles the page routing by including the Routes logic from the
 * routes.js file. The Router is dependend on the page.js and axios libraries,
 * which have to be including in the project bootstrap to work.
 */
export default class Router {
    constructor(instance) {
        // Load transition definitions
        this.transitions = Transitions;
        this.instance = instance;

        // enhance the original axios adapter with throttle and cache enhancer
        this.http = axios.create({
            baseURL: '/',
            headers: { 'Cache-Control': 'no-cache' },
            adapter: throttleAdapterEnhancer(cacheAdapterEnhancer(axios.defaults.adapter)),
        });

        // Let's go
        this.init();
    }

    init() {
        const routes = new Map();
        routes.set('/', this.startTransition.bind(this));
        routes.set('*', this.startTransition.bind(this));

        // Register page routes
        routes.forEach((routeHandler, path) => {
            page(path, routeHandler);
        });

        // Start page.js, without first dispatch
        // This without prevent page.js to use transitions on first load
        page({ dispatch: false, popstate: false });

        window.onpopstate = () => {
            window.location.reload();
        };
    }

    cachePath(path) {
        this.http.get(path);
    }

    /**
     * Set the application transition.
     *
     * @param {string} transition
     * @return {void}
     */
    setTransition(transition) {
        // Create a flat array from the transitions object
        const strings = _values(this.transitions);

        // Set the transition if it exists or if it's default
        if (strings.indexOf(transition) || transition === 'default') {
            this.transition = transition;
            return;
        }

        // Alert if the transition does not exist
        console.log('Transition "' + transition + '" does not exist. Be sure to set the correct transition or use "default".');
    }

    /**
     * Called when the page transition is going to start.
     *
     * @return {void}
     */
    pageTransitionStart() {
        console.log('Calling the router completion handler');
    }

    /**
     * Starts the page transition.
     *
     * @param {Object}   ctx
     * @param {Function} next
     * @return {void}
     */
    startTransition(ctx, next) {
        if (this.inTransition) {
            return;
        }

        this.inTransition = true;

        this.destroyPage();

        window.requestAnimationFrame(() => {
            this.http.get(ctx.path)
                .then((response) => {
                    switch (this.transition) {
                    case this.transitions.HomepageSliderToWork:
                        TransitionHomepageSliderToWork(response, ctx);
                        break;
                    case this.transitions.ContextSliderToWork:
                        TransitionContextSliderToWork(response, ctx);
                        break;
                    case this.transitions.DefaultFromMenu:
                        TransitionDefaultFromMenu(response, ctx);
                        break;
                    case this.transitions.OverviewToStory:
                        TransitionOverviewToStory(response, ctx);
                        break;
                    case this.transitions.DefaultToHomepage:
                        TransitionDefaultToHomepage(response, ctx);
                        break;
                    case this.transitions.RelatedStory:
                        TransitionRelatedStory(response, ctx);
                        break;
                    default:
                        TransitionDefault(response, ctx);
                        break;
                    }
                })
                .catch(this.onTransitionError);
        });
    }

    /**
     * Called when the page transition is completed. Normally, everything
     * should already be taken care of. However, this is the place
     * where you can do any additional page transtion cleaning
     *
     * @return {void}
     */
    pageTransitionComplete() {
        // Reset the page transition
        this.transition = 'default';

        const pageInfo = document.getElementById('page-info');
        const currentSection = this.instance.section;
        if (pageInfo) {
            // Title
            document.title = pageInfo.getAttribute('data-title');

            // Section
            this.instance.section = pageInfo.getAttribute('data-section');
            document.documentElement.classList.remove(currentSection);
            document.documentElement.classList.add(this.instance.section);

            // Initialize header state.
            App.darkHeader = (pageInfo.getAttribute('data-initial-header') === '1');

            // Clean up
            pageInfo.parentNode.removeChild(pageInfo);
        }

        // Remove all current page content
        const currentContent = [].slice.call(document.querySelectorAll('.current-page-content'));

        for (let i = 0; i < currentContent.length; i++) {
            const child = currentContent[i];
            child.parentNode.removeChild(child);
        }

        // Make sure the new content is added, and the 'ga()' method is available.
        if (typeof ga === 'function') {
            ga('set', {
                page: window.location.pathname,
                title: document.title,
            });
            ga('send', 'pageview');
        }

        // Emit completion event
        // !! Do not remove
        EventBus.$emit('page-transition-enter-complete');
        this.inTransition = false;
    }

    /**
     * Raised when there was an error fetching the ajax data using axios.
     *
     * @param  {String} error
     * @return {void}
     */
    onTransitionError(error) {
        console.log(error);
    }

    /**
     * (Re)build the Vue instance by using the Page component
     *
     * @param  {DOMElement} el
     * @return {void}
     */
    buildPage(el) {
        const page = new Page({ el, parent: this.instance }); // eslint-disable-line no-unused-vars
    }

    /**
     * Destroys the current Vue instances on the page,
     * usefull before starting a transition.
     *
     * @return {void}
     */
    destroyPage() {
        for (let index = 0; index < this.instance.$children.length; index++) {
            const child = this.instance.$children[index];
            this.destroyChildComponent(child);
        }
    }

    destroyChildComponent(component) {
        for (let index = 0; index < component.$children.length; index++) {
            const child = component.$children[index];
            this.destroyChildComponent(child);
        }

        if (component === undefined) {
            return;
        }

        if (component.$options.name === 'footer-component') {
            return;
        }

        // Check if the child is a Vue vm instance
        if (typeof component.$destroy !== 'undefined') {
            component.$destroy(true);
        }
    }
}



// WEBPACK FOOTER //
// ./resources/assets/js/router.js